import React, { useEffect, useState } from "react";
import { Loader2, Share2, Linkedin, MessageCircle, Mail, Link2, ArrowDownRight, Users, Inbox } from "lucide-react";
import { adminApi } from "../../lib/api";

const PERIODS = [7, 30, 90];

const NETWORK_ICON = { linkedin: Linkedin, whatsapp: MessageCircle, email: Mail, copy: Link2 };
const NETWORK_LABEL = {
  linkedin: "LinkedIn", whatsapp: "WhatsApp", email: "Email", copy: "Copied link",
  facebook: "Facebook", x: "X", instagram: "Instagram", other: "Elsewhere",
};

const Card = ({ title, subtitle, children, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid={testid}>
    <h3 className="font-display font-bold text-navy">{title}</h3>
    {subtitle && <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">{subtitle}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

const Stat = ({ icon: Icon, label, value, hint }) => (
  <div className="rounded-xl bg-slate-50 p-4">
    <div className="flex items-center gap-1.5 text-slate-400">
      <Icon size={14} />
      <span className="text-xs">{label}</span>
    </div>
    <p className="text-2xl font-display font-bold text-navy mt-1.5">{value}</p>
    {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

const Bars = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => Math.max(d.shares, d.arrivals)));
  return (
    <div className="flex items-end gap-[3px] h-24" data-testid="share-report-trend">
      {daily.map((d) => (
        <div key={d.date} className="flex-1 group relative flex items-end justify-center gap-[1px] h-full">
          <div className="w-1/2 rounded-t bg-navy/80 min-h-[2px]" style={{ height: `${(d.shares / max) * 100}%` }} />
          <div className="w-1/2 rounded-t bg-orange/80 min-h-[2px]" style={{ height: `${(d.arrivals / max) * 100}%` }} />
          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-lg bg-navy text-white text-[11px] px-2 py-1 z-10">
            {d.date} · {d.shares} shared · {d.arrivals} arrived
          </span>
        </div>
      ))}
    </div>
  );
};

const PageList = ({ rows, testid, empty }) =>
  rows.length === 0 ? (
    <p className="text-sm text-slate-400">{empty}</p>
  ) : (
    <ul className="divide-y divide-slate-100" data-testid={testid}>
      {rows.map((r) => (
        <li key={r.path} className="py-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <a href={r.path} target="_blank" rel="noreferrer" className="font-semibold text-navy hover:text-orange text-sm">
              {r.title || r.path}
            </a>
            <span className="block text-[11px] text-slate-400">{r.path}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(r.networks || []).map((n) => (
              <span key={n.network} className="text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">
                {NETWORK_LABEL[n.network] || n.network} {n.count}
              </span>
            ))}
            <span className="text-sm font-bold text-navy w-8 text-right">{r.count}</span>
          </div>
        </li>
      ))}
    </ul>
  );

export default function ShareReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi.shareCards.report(days).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [days]);

  if (loading && !data) return <p className="text-sm text-slate-400">Loading…</p>;
  if (!data) return <p className="text-sm text-red-500">Couldn't load the share figures.</p>;

  const t = data.totals;

  return (
    <div data-testid="share-report">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setDays(p)}
              data-testid={`share-report-period-${p}`}
              className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors ${
                days === p ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-orange"
              }`}
            >
              {p} days
            </button>
          ))}
        </div>
        {loading && <Loader2 size={15} className="animate-spin text-slate-400" />}
      </div>

      <Card
        title={`Shares — last ${data.days} days`}
        subtitle="Counted on our own server and anonymously: a share button being pressed, somebody arriving from a shared link or a social network, and whether that visit went on to a page that matters. It shows interest, not income."
        testid="share-report-summary"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={Share2} label="Shares" value={t.shares} hint={`${t.shared_pages} different page${t.shared_pages === 1 ? "" : "s"}`} />
          <Stat icon={Users} label="Arrived from a share" value={t.arrival_visits} hint={`${t.arrivals_per_share} per share`} />
          <Stat icon={ArrowDownRight} label="Went on to look" value={t.onward} hint={`${t.onward_rate}% of arrivals`} />
          <Stat
            icon={Inbox}
            label="Enquiries from shares"
            value={t.enquiries ?? 0}
            hint={data.networks[0] ? `most used: ${NETWORK_LABEL[data.networks[0].network] || data.networks[0].network}` : ""}
          />
        </div>

        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-wide font-bold text-slate-400 mb-2">
            Per day — <span className="text-navy">shared</span> and <span className="text-orange">arrived</span>
          </p>
          <Bars daily={data.daily} />
        </div>

        {data.tracked_since && (
          <p className="text-[11px] text-slate-400 mt-3">Counting began {String(data.tracked_since).slice(0, 10)}</p>
        )}
      </Card>

      <Card title="Which network" subtitle="Where links were sent, and where people came back from." testid="share-report-networks-card">
        {data.networks.length === 0 ? (
          <p className="text-sm text-slate-400">Nothing shared yet in this period.</p>
        ) : (
          <ul className="space-y-2.5" data-testid="share-report-networks">
            {data.networks.map((n) => {
              const Icon = NETWORK_ICON[n.network] || Share2;
              const max = Math.max(1, ...data.networks.map((x) => x.shares));
              return (
                <li key={n.network} className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 text-xs text-slate-500 w-32 shrink-0">
                    <Icon size={13} /> {NETWORK_LABEL[n.network] || n.network}
                  </span>
                  <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-navy rounded-full" style={{ width: `${(n.shares / max) * 100}%` }} />
                  </div>
                  <span className="text-xs font-bold text-navy w-10 text-right">{n.shares}</span>
                  <span className="text-[11px] text-slate-400 w-40 text-right">
                    {n.arrivals} arrived · {n.enquiries ?? 0} enquir{(n.enquiries ?? 0) === 1 ? "y" : "ies"}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card title="What gets shared" subtitle="The pages people choose to pass on." testid="share-report-shared-card">
        <PageList rows={data.shared_pages} testid="share-report-shared" empty="No shares recorded in this period." />
      </Card>

      <Card title="Where they land" subtitle="The pages visitors arrive on when they follow a shared link." testid="share-report-arrivals-card">
        <PageList rows={data.arrival_pages} testid="share-report-arrivals" empty="Nobody has arrived from a shared link yet." />
      </Card>

      <Card
        title="Enquiries that trace back to a share"
        subtitle="An enquiry, guide download or package builder submission from somebody whose first visit came from a shared link or a social network. The trail is kept for 30 days, so a slow decision still gets credited."
        testid="share-report-enquiries-card"
      >
        {(data.enquiries || []).length === 0 ? (
          <p className="text-sm text-slate-400">None yet in this period.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="share-report-enquiries">
            {data.enquiries.map((e, i) => (
              <li key={`${e.created_at}-${i}`} className="py-3 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-navy">
                    {e.name || "Someone"} <span className="font-normal text-slate-400">· {e.kind}</span>
                  </p>
                  <span className="block text-[11px] text-slate-400">
                    {NETWORK_LABEL[e.network] || e.network}
                    {e.landing ? ` · first landed on ${e.landing}` : ""}
                    {e.campaign ? ` · ${e.campaign}` : ""}
                  </span>
                </div>
                <span className="text-[11px] text-slate-400 shrink-0">{String(e.created_at).slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        title="What it brought back"
        subtitle="Pages those visitors went on to after arriving — contact, pricing, services or the guides."
        testid="share-report-onward-card"
      >
        {data.onward_pages.length === 0 ? (
          <p className="text-sm text-slate-400">No onward visits recorded yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="share-report-onward">
            {data.onward_pages.map((r) => (
              <li key={r.path} className="py-2.5 flex items-center justify-between">
                <span className="text-sm text-navy font-semibold">{r.path}</span>
                <span className="text-sm font-bold text-navy">{r.count}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
